/**
 * FE-PR-20 / G7 — scheduled pickup (LATER) availability for collect sheet.
 * P3 — also surfaces TENANT opsMode/opsBufferMs from the same pickup-slots probe
 * so Mode S pause/delay messaging can render without mounting a Mode S slot calendar.
 *
 * Optional capability: requires parent `order_pickup_infrastructure` and API
 * `scheduled_pickup` (pickup-slots). Customer surface snapshot exposes infra but
 * not `scheduled_pickup` — gate on infra before probing so LATER never flashes
 * when infra is Off; keep probe (403 → disabled) for scheduled_pickup alone.
 */
import { useQuery } from '@tanstack/react-query';
import { useCustomerSurfaceEntitlement } from '../../entitlements/customerEntitlementGuards.js';
import {
  fetchPickupSlots,
  type TenantOpsMode,
} from '../../checkout/pickupSlotsApi.js';

export type ShopPickupOpsMode = TenantOpsMode;

export interface ShopScheduledPickupState {
  readonly enabled: boolean;
  readonly opsMode: ShopPickupOpsMode;
  readonly opsBufferMs: number | null;
}

const DEFAULT_SCHEDULED_PICKUP_STATE: ShopScheduledPickupState = {
  enabled: false,
  opsMode: 'NORMAL',
  opsBufferMs: null,
};

export function useShopScheduledPickup(input: {
  readonly tenantCode: string;
  readonly kioskId: number | null;
  readonly pickupPointId: number | undefined;
  readonly pickupPointsLoaded: boolean;
}): ShopScheduledPickupState {
  const { isBlockEntitled, isLoading: entitlementLoading } = useCustomerSurfaceEntitlement();
  // No scheduled_pickup in customer surface snapshot — parent infra is the client gate.
  const pickupInfraEntitled = isBlockEntitled('order_pickup_infrastructure');

  const scheduledPickupProbe = useQuery({
    queryKey: [
      'shop-scheduled-pickup',
      input.tenantCode,
      input.kioskId,
      input.pickupPointId,
      pickupInfraEntitled,
    ],
    queryFn: async (): Promise<ShopScheduledPickupState> => {
      if (input.kioskId === null || input.kioskId <= 0) {
        return DEFAULT_SCHEDULED_PICKUP_STATE;
      }
      if (input.pickupPointId === undefined) {
        return DEFAULT_SCHEDULED_PICKUP_STATE;
      }
      const from = new Date();
      const to = new Date(from.getTime() + 7 * 24 * 60 * 60_000);
      try {
        const data = await fetchPickupSlots({
          tenantCode: input.tenantCode,
          kioskId: input.kioskId,
          pickupPointId: input.pickupPointId,
          from: from.toISOString(),
          to: to.toISOString(),
        });
        const available = data.slots.filter((slot) => slot.available);
        // PAUSE returns empty slots — keep LATER off; still expose opsMode for UX.
        return {
          enabled: data.opsMode !== 'PAUSE' && available.length > 0,
          opsMode: data.opsMode,
          opsBufferMs: data.opsBufferMs,
        };
      } catch {
        // Entitlement deny (scheduled_pickup) / not configured — optional feature off.
        return DEFAULT_SCHEDULED_PICKUP_STATE;
      }
    },
    enabled:
      !entitlementLoading &&
      pickupInfraEntitled &&
      input.kioskId !== null &&
      input.kioskId > 0 &&
      input.pickupPointsLoaded &&
      input.pickupPointId !== undefined,
    retry: false,
  });

  if (entitlementLoading || !pickupInfraEntitled) {
    return DEFAULT_SCHEDULED_PICKUP_STATE;
  }
  return scheduledPickupProbe.data ?? DEFAULT_SCHEDULED_PICKUP_STATE;
}

type ShopScheduledPickupInput = {
  readonly tenantCode: string;
  readonly kioskId: number | null;
  readonly pickupPointId: number | undefined;
  readonly pickupPointsLoaded: boolean;
};

/** G7 — boolean LATER gate (same probe as {@link useShopScheduledPickup}). */
export function useShopScheduledPickupEnabled(input: ShopScheduledPickupInput): boolean {
  return useShopScheduledPickup(input).enabled;
}

/** P3 — TENANT ops mode from the shared pickup-slots probe (Mode S messaging only). */
export function useShopTenantOpsMode(input: ShopScheduledPickupInput): TenantOpsMode {
  return useShopScheduledPickup(input).opsMode;
}
